import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Input,
  Form,
  Row,
  Col,
  message,
  Select,
  Switch,
  Breadcrumb,
} from "antd";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import GoBackButton from "../../../components/common/buttons/GoBackButton";
import { Link, useNavigate, useParams } from "react-router-dom";
import { mutationOnErrorHandler } from "../../../utils/mutationUtils";
import { useEffect } from "react";
import {
  getContactByIdRequest,
  updateContactRequest,
  updateSystemUserRequest,
} from "../../../api/requests/contact";
import WelcomeTopbar from "../../../components/common/header/WelcomeTopbar";
import { useContactById } from "../../../api/hooks/customer";

const updateContactSchemaResolver = yupResolver(
  yup.object().shape({
    active: yup.boolean(),
    username: yup.string(),
    password: yup.string(),
  }),
);

function ContactUpdateSystemUser() {
  const params = useParams();
  const { id } = params;
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: contactDetails } = useContactById({ id });

  const { mutateAsync: updateSystemUser, isPending: updatingSystemUser } =
    useMutation({
      mutationFn: async (data) => {
        const res = await updateSystemUserRequest({ contactId: id, data });
        return res.data;
      },
      mutationKey: [`user/update-system-user/${id}`],
      onSuccess: (res) => {
        const successMessage = res?.message;
        if (successMessage) {
          message.success(successMessage);
        }
        navigate(-1);
      },
      onError: (error) => {
        mutationOnErrorHandler({ error, message });
      },
    });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: updateContactSchemaResolver,
  });

  // console.log("errors---->", errors);

  async function onSubmit(data) {
    // console.log(data);
    const res = await updateSystemUser(data);
    // console.log("res----->", res);
  }

  useEffect(() => {
    if (contactDetails) {
      const { systemUser = {} } = contactDetails?.data;
      const { username, active } = systemUser;

      reset({
        active,
        username,
      });
    }
  }, [contactDetails, reset]);

  return (
    <div className="flex flex-col">
      <WelcomeTopbar />
      <div className="flex flex-col">
        <div className="flex flex-col gap-6">
          <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
            <div className="flex items-center justify-between rounded-t-xl bg-[#D1DFFF] px-6 py-3">
              <Breadcrumb
                items={[
                  {
                    title: <Link to={"/setup/contacts"}>Contacts</Link>,
                  },
                  {
                    title: (
                      <Link to={`/setup/contacts/update/${id}`}>Update</Link>
                    ),
                  },
                  {
                    title: <div className="text-lg">System User</div>,
                  },
                ]}
              />
              <Button
                type="primary"
                htmlType="submit"
                className="flex items-center justify-center"
                loading={updatingSystemUser}
              >
                Update
              </Button>
            </div>
            <div className="px-6 py-6">
              <Row gutter={18}>
                <Col span={12}>
                  <Form.Item
                    label="Username"
                    name="username"
                    validateStatus={errors.username ? "error" : ""}
                    help={errors?.username?.message}
                  >
                    <Controller
                      control={control}
                      name="username"
                      render={({ field }) => (
                        <Input
                          {...field}
                          placeholder="Username"
                          autoComplete="system-user-name"
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Password"
                    name="password"
                    validateStatus={errors.password ? "error" : ""}
                    help={errors?.password?.message}
                  >
                    <Controller
                      control={control}
                      name="password"
                      render={({ field }) => (
                        <Input.Password
                          {...field}
                          autoComplete="new-password"
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Profile Picture"
                    name="profilePicture"
                    validateStatus={errors.profilePicture ? "error" : ""}
                    help={errors?.profilePicture?.message}
                  >
                    <Controller
                      control={control}
                      name="profilePicture"
                      render={({ field }) => <Input type="file" {...field} />}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Active"
                    name="active"
                    validateStatus={errors.active ? "error" : ""}
                    help={errors.active && errors.active.message}
                  >
                    <Controller
                      control={control}
                      name="active"
                      render={({ field }) => <Switch {...field} />}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
}

export default ContactUpdateSystemUser;
